import { ArrearsState, ArrearsAction } from '../types';
import { getAmountBasedOnType } from '../utils';

export const initialState: ArrearsState = {
  months: {},
  editHistory: {},
  previewCache: undefined,
};

const addEditFlag = (
  state: ArrearsState,
  arrearId: string,
  field: 'month' | 'days' | 'components',
): ArrearsState => {
  const currentFields = state.editHistory[arrearId]?.fieldsChanged || new Set();
  const newFields = new Set(currentFields);
  newFields.add(field);

  return {
    ...state,
    editHistory: {
      ...state.editHistory,
      [arrearId]: {
        fieldsChanged: newFields,
        isDeleted: state.editHistory[arrearId]?.isDeleted || false,
        originalData: state.editHistory[arrearId]?.originalData || state.months[arrearId],
      },
    },
  };
};

export const arrearsReducer = (state: ArrearsState, action: ArrearsAction): ArrearsState => {
  switch (action.type) {
    case 'FETCH_SUCCESS': {
      // Initialize state from API with empty edit history
      const editHistory: ArrearsState['editHistory'] = {};
      Object.keys(action.payload).forEach((arrearId) => {
        editHistory[arrearId] = {
          fieldsChanged: new Set(),
          isDeleted: false,
          originalData: action.payload[arrearId],
        };
      });

      return {
        months: action.payload,
        editHistory,
        previewCache: undefined,
      };
    }

    case 'MONTH_CHANGED': {
      const { arrearId, month } = action.payload;
      const updatedState = {
        ...state,
        months: {
          ...state.months,
          [arrearId]: {
            ...state.months[arrearId],
            sourceMonth: month,
          },
        },
      };
      return addEditFlag(updatedState, arrearId, 'month');
    }

    case 'DAYS_CHANGED': {
      const { arrearId, days } = action.payload;
      const updatedState = {
        ...state,
        months: {
          ...state.months,
          [arrearId]: {
            ...state.months[arrearId],
            arrearDays: days,
          },
        },
      };
      return addEditFlag(updatedState, arrearId, 'days');
    }

    case 'COMPONENT_ADDED': {
      const { arrearId, component } = action.payload;
      const updatedState = {
        ...state,
        months: {
          ...state.months,
          [arrearId]: {
            ...state.months[arrearId],
            components: [...state.months[arrearId].components, component],
          },
        },
      };
      return addEditFlag(updatedState, arrearId, 'components');
    }

    case 'COMPONENT_DELETED': {
      const { arrearId, componentId, isEsiArrearEnabled, isPfArrearEnabled } = action.payload;
      const updatedComponents = state.months[arrearId].components.filter(
        (c) => c.id !== componentId,
      );

      // Add PF/ESI components if needed
      if (isEsiArrearEnabled && updatedComponents.length > 0) {
        const hasEsiComponent = updatedComponents.some((c) => c.sdiType === 'esi_v1');

        if (!hasEsiComponent) {
          updatedComponents.push({
            id: 'esi_v1',
            componentName: 'ESIC',
            amount: 0,
            sdiType: 'esi_v1',
            componentId: 'esi_v1',
            isSaved: false,
          });
        }
      }
      if (isPfArrearEnabled && updatedComponents.length > 0) {
        const hasPfComponent = updatedComponents.some((c) => c.sdiType === 'pf_v1');

        if (!hasPfComponent) {
          updatedComponents.push({
            id: 'pf_v1',
            componentName: 'PF',
            amount: 0,
            sdiType: 'pf_v1',
            componentId: 'pf_v1',
            isSaved: false,
          });
        }
      }
      const updatedState = {
        ...state,
        months: {
          ...state.months,
          [arrearId]: {
            ...state.months[arrearId],
            components: updatedComponents,
          },
        },
      };
      return addEditFlag(updatedState, arrearId, 'components');
    }

    case 'COMPONENT_UPDATED': {
      const { arrearId, componentId, updates } = action.payload;
      const updatedState = {
        ...state,
        months: {
          ...state.months,
          [arrearId]: {
            ...state.months[arrearId],
            components: state.months[arrearId].components.map((c) =>
              c.id === componentId ? { ...c, ...updates } : c,
            ),
          },
        },
      };
      return addEditFlag(updatedState, arrearId, 'components');
    }

    case 'COMPONENT_AMOUNT_CHANGED': {
      const { arrearId, componentId, amount, isEsiArrearEnabled, isPfArrearEnabled } =
        action.payload;

      let updatedComponents = state.months[arrearId].components.map((c) =>
        c.id === componentId ? { ...c, amount } : c,
      );

      // Add PF/ESI components if needed
      if (isEsiArrearEnabled) {
        const hasEsiComponent = updatedComponents.some((c) => c.sdiType === 'esi_v1');

        if (!hasEsiComponent) {
          updatedComponents.push({
            id: 'esi_v1',
            componentName: 'ESIC',
            amount: 0,
            sdiType: 'esi_v1',
            componentId: 'esi_v1',
            isSaved: false,
          });
        }
      }
      if (isPfArrearEnabled) {
        const hasPfComponent = updatedComponents.some((c) => c.sdiType === 'pf_v1');

        if (!hasPfComponent) {
          updatedComponents.push({
            id: 'pf_v1',
            componentName: 'PF',
            amount: 0,
            sdiType: 'pf_v1',
            componentId: 'pf_v1',
            isSaved: false,
          });
        }
      }

      const updatedState = {
        ...state,
        months: {
          ...state.months,
          [arrearId]: {
            ...state.months[arrearId],
            components: updatedComponents,
          },
        },
      };
      return addEditFlag(updatedState, arrearId, 'components');
    }

    case 'MONTH_ADDED': {
      const newMonth = action.payload;
      return {
        ...state,
        months: {
          ...state.months,
          [newMonth.arrearId]: newMonth,
        },
        editHistory: {
          ...state.editHistory,
          [newMonth.arrearId]: {
            fieldsChanged: new Set(['month', 'days', 'components']),
            isDeleted: false,
            originalData: undefined,
          },
        },
      };
    }

    case 'MONTH_DELETED': {
      const { arrearId } = action.payload;
      const month = state.months[arrearId];

      // If it's not saved, remove it completely
      if (!month.isSaved) {
        const { [arrearId]: removed, ...remainingMonths } = state.months;
        const { [arrearId]: removedHistory, ...remainingHistory } = state.editHistory;
        return {
          ...state,
          months: remainingMonths,
          editHistory: remainingHistory,
        };
      }

      // If saved, mark as deleted
      return {
        ...state,
        editHistory: {
          ...state.editHistory,
          [arrearId]: {
            ...state.editHistory[arrearId],
            isDeleted: true,
            fieldsChanged: new Set(),
          },
        },
      };
    }

    case 'PREVIEW_COMPUTED': {
      const { arrears, requestId } = action.payload;

      // Update compliance component amounts (PF/ESI) based on preview
      const updatedMonths = { ...state.months };

      arrears.forEach((arrearPreview) => {
        // Find the matching month by sourceMonth
        const matchingArrearId = Object.keys(updatedMonths).find(
          (id) => updatedMonths[id].sourceMonth === arrearPreview.sourceMonth,
        );

        if (matchingArrearId && arrearPreview.arrearComponents) {
          const pfComponent = arrearPreview.arrearComponents.find((c) => c.sdiType === 'pf_v1');
          const esiComponent = arrearPreview.arrearComponents.find((c) => c.sdiType === 'esi_v1');

          updatedMonths[matchingArrearId] = {
            ...updatedMonths[matchingArrearId],
            components: updatedMonths[matchingArrearId].components.map((c) => {
              if (c.sdiType === 'pf_v1' && pfComponent) {
                return {
                  ...c,
                  amount: getAmountBasedOnType(pfComponent.sdiType, pfComponent.formulae),
                };
              }
              if (c.sdiType === 'esi_v1' && esiComponent) {
                return {
                  ...c,
                  amount: getAmountBasedOnType(esiComponent.sdiType, esiComponent.formulae),
                };
              }
              return c;
            }),
          };
        }
      });

      return {
        ...state,
        months: updatedMonths,
        previewCache: { requestId },
      };
    }

    case 'DELETE_COMPLETED': {
      const { arrearIds } = action.payload;

      // Reset specific months
      const updatedHistory = { ...state.editHistory };
      arrearIds.forEach((id) => {
        if (updatedHistory[id]) {
          updatedHistory[id] = {
            ...updatedHistory[id],
            fieldsChanged: new Set(),
            isDeletedFromDb: true,
          };
        }
      });
      return { ...state, editHistory: updatedHistory };
    }

    case 'CLEAR_PREVIEW_CACHE': {
      return {
        ...state,
        previewCache: undefined,
      };
    }

    default:
      return state;
  }
};
